import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Loading, ErrorState } from "@/components/common/States";
import WhatsAppButton from "@/components/common/WhatsAppButton";
import StatusBadge from "@/components/common/StatusBadge";
import EmptyState from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtCurrency } from "@/lib/format";
import { ArrowLeft, Phone, Mail, MapPin, UserPlus, Plus, Stethoscope } from "lucide-react";

export default function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [acc, setAcc] = useState(null);
  const [error, setError] = useState("");
  const [contactOpen, setContactOpen] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [contact, setContact] = useState({ name: "", designation: "", mobile: "", email: "" });
  const [loc, setLoc] = useState({ label: "", address: "", city: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try { const { data } = await api.get(`/accounts/${id}`); setAcc(data); }
    catch (e) { setError(apiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const addContact = async () => {
    if (!contact.name.trim()) return toast.error("Contact name required");
    setBusy(true);
    try { await api.post(`/accounts/${id}/contacts`, contact); toast.success("Contact added"); setContactOpen(false); setContact({ name: "", designation: "", mobile: "", email: "" }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const addLoc = async () => {
    setBusy(true);
    try { await api.post(`/accounts/${id}/locations`, loc); toast.success("Location added"); setLocOpen(false); setLoc({ label: "", address: "", city: "" }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!acc) return <Loading label="Loading account…" />;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/accounts")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal" data-testid="account-back-btn"><ArrowLeft className="h-4 w-4" /> All accounts</button>
      <PageHeader title={acc.name} subtitle={`${acc.account_type} · ${acc.city || "—"}`} testid="account-detail-header"
        actions={<WhatsAppButton phone={acc.phone} message={`Hello, regarding ${acc.name}.`} accountId={id} />} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Doctor contacts</h3>
              <Button size="sm" variant="outline" onClick={() => setContactOpen(true)} data-testid="add-contact-btn"><UserPlus className="h-4 w-4" /> Add</Button>
            </div>
            {(!acc.contacts || acc.contacts.length === 0) ? <EmptyState title="No contacts" description="Add doctor contacts for this account." icon={Stethoscope} /> : (
              <div className="space-y-3">
                {acc.contacts.map((c) => (
                  <div key={c.id} className="flex items-center justify-between border border-slate-100 rounded-lg p-3" data-testid={`contact-${c.id}`}>
                    <div className="min-w-0"><div className="font-semibold text-slate-800">{c.name}</div><div className="text-xs text-slate-400">{c.designation || c.specialty || "—"} · {c.mobile || "—"}</div></div>
                    {c.mobile && <WhatsAppButton phone={c.whatsapp || c.mobile} message={`Hello Dr. ${c.name}, from Medoho.`} accountId={id} label="Chat" />}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Deals</h3>
            {(!acc.deals || acc.deals.length === 0) ? <EmptyState title="No deals" description="Deals for this account will appear here." /> : (
              <div className="space-y-2">
                {acc.deals.map((d) => (
                  <div key={d.id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0">
                    <span className="text-sm font-medium text-slate-700">{d.title}</span>
                    <span className="flex items-center gap-3 text-sm"><b>{fmtCurrency(d.amount)}</b><StatusBadge status={d.stage} label={d.stage} /></span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6 space-y-4">
            <h3 className="text-sm font-semibold text-slate-900">Details</h3>
            <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-slate-400" /><span className="text-sm">{acc.phone || "—"}</span></div>
            <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-slate-400" /><span className="text-sm">{acc.email || "—"}</span></div>
            <div className="flex items-center gap-3"><MapPin className="h-4 w-4 text-slate-400" /><span className="text-sm">{acc.address || "—"}</span></div>
          </div>
          <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-3"><h3 className="text-sm font-semibold text-slate-900">Locations</h3><Button size="sm" variant="outline" onClick={() => setLocOpen(true)} data-testid="add-location-btn"><Plus className="h-4 w-4" /></Button></div>
            {(!acc.locations || acc.locations.length === 0) ? <p className="text-sm text-slate-400">No locations</p> : (
              <div className="space-y-2">{acc.locations.map((l) => <div key={l.id} className="text-sm text-slate-700"><b>{l.label || "Location"}</b><div className="text-xs text-slate-400">{l.address} {l.city}</div></div>)}</div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent data-testid="contact-dialog">
          <DialogHeader><DialogTitle>Add contact</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input className="mt-1.5" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="contact-name-input" /></div>
            <div><Label>Designation / specialty</Label><Input className="mt-1.5" value={contact.designation} onChange={(e) => setContact({ ...contact, designation: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Mobile</Label><Input className="mt-1.5" value={contact.mobile} onChange={(e) => setContact({ ...contact, mobile: e.target.value })} /></div>
              <div><Label>Email</Label><Input className="mt-1.5" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setContactOpen(false)}>Cancel</Button><Button onClick={addContact} disabled={busy} className="bg-teal hover:bg-teal-700" data-testid="save-contact-btn">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={locOpen} onOpenChange={setLocOpen}>
        <DialogContent data-testid="location-dialog">
          <DialogHeader><DialogTitle>Add location</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Label</Label><Input className="mt-1.5" value={loc.label} onChange={(e) => setLoc({ ...loc, label: e.target.value })} /></div>
            <div><Label>Address</Label><Input className="mt-1.5" value={loc.address} onChange={(e) => setLoc({ ...loc, address: e.target.value })} /></div>
            <div><Label>City</Label><Input className="mt-1.5" value={loc.city} onChange={(e) => setLoc({ ...loc, city: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setLocOpen(false)}>Cancel</Button><Button onClick={addLoc} disabled={busy} className="bg-teal hover:bg-teal-700" data-testid="save-location-btn">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
